import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import type { FC } from "react";
import toast from "react-hot-toast";
import { useSupabaseCallback } from "../../server/supabase/hooks";
import type { UUID } from "../../utils/uuid";
import { SupabaseFolderService } from "./server/folder-service";

export type ArchiveFolderData = {
  name: string;
  folderId: UUID;
};

export const ArchiveFolderModal: FC<{
  open: boolean;
  onClose: () => void;
  reloadFolders: () => void;
  name: string;
  folderId: UUID;
}> = ({ open, onClose, reloadFolders, name, folderId }) => {
  const handleArchive = useSupabaseCallback(
    async ({ supabase }) => {
      const service = new SupabaseFolderService(supabase);

      const { error } = await service.deleteFolderAndContents(folderId);

      if (error) {
        console.error(error);
        toast.error("Error archiving folder");
        return false;
      }

      toast.success("Folder and its contents archived");
      reloadFolders();
      onClose();
      return true;
    },
    [folderId, reloadFolders, onClose],
  );

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Archive the folder {name} and all its contents?</DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 2 }}>
          Archiving this folder will hide it and all of its contents from your Content Library.
          Embedded links to items inside the folder will continue to work, but no one will be able
          to browse to the folder or save new items to it.
        </Typography>
        <Typography sx={{ mb: 2 }}>
          If you want to permanently delete the folder and everything in it, contact
          support@changeengine.com.
        </Typography>
        <Typography>
          You can move any files and folders you want to keep to another folder.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Stack direction="row" spacing={2} sx={{ width: "100%", p: 1, justifyContent: "flex-end" }}>
          <Button data-analytics-id="archive-folder-cancel" variant="text" onClick={onClose}>
            Cancel
          </Button>
          <Button
            data-analytics-id="archive-folder-confirm"
            variant="text"
            onClick={() => void handleArchive()}
            color="error"
          >
            Archive this folder and all of its contents
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};
